import { useState } from "react";
import { useTheme } from "../../../contexts/ThemeContext";
import type { ShowcaseListing } from "../../../mocks/showcaseMocks";

interface ShowcaseCardProps {
  listing: ShowcaseListing;
  isInterested: boolean;
  onInterest: (id: string, sellerName: string) => void;
}

export default function ShowcaseCard({ listing, isInterested, onInterest }: ShowcaseCardProps) {
  const { isDark } = useTheme();
  const [imgError, setImgError] = useState(false);
  const profit = listing.sellPrice - listing.buyPrice;
  const margin = Math.round((profit / listing.buyPrice) * 100);
  const isHot = listing.interestedCount >= 20;

  const marginColor =
    margin >= 50 ? "text-amber-400" : margin >= 20 ? "text-emerald-400" : isDark ? "text-[#888]" : "text-[#666]";

  return (
    <div className={`rounded-2xl border overflow-hidden flex flex-col transition-all duration-200 hover:-translate-y-0.5 ${isDark ? "bg-white/[0.02] border-white/6 hover:border-white/12" : "bg-white border-black/8 hover:border-black/14"}`}>
      {/* Image */}
      <div className={`relative w-full h-44 overflow-hidden flex-shrink-0 ${isDark ? "bg-white/4" : "bg-black/4"}`}>
        {!imgError ? (
          <img
            src={listing.image}
            alt={listing.title}
            className="w-full h-full object-cover object-top"
            onError={() => setImgError(true)}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <i className={`ri-image-line text-3xl ${isDark ? "text-white/10" : "text-black/10"}`} />
          </div>
        )}
        {/* Badges */}
        <div className="absolute top-3 left-3 flex items-center gap-1.5">
          <span className={`font-inter text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full font-medium ${isDark ? "bg-black/70 text-white/70" : "bg-white/85 text-[#333]"}`}>
            {listing.category}
          </span>
          {isHot && (
            <span className="font-inter text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full font-medium bg-amber-500/90 text-white">
              Hot
            </span>
          )}
        </div>
        {/* Condition */}
        <div className="absolute top-3 right-3">
          <span className={`font-inter text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full font-medium ${isDark ? "bg-black/70 text-white/60" : "bg-white/85 text-[#555]"}`}>
            {listing.condition}
          </span>
        </div>
      </div>

      {/* Body */}
      <div className="p-5 flex flex-col gap-4 flex-1">
        {/* Seller */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-white font-inter font-bold text-[9px] ${listing.seller.color}`}>
              {listing.seller.initial}
            </div>
            <span className={`font-inter text-xs ${isDark ? "text-[#666]" : "text-[#888]"}`}>
              {listing.seller.name}
            </span>
          </div>
          <span className={`font-inter text-[10px] ${isDark ? "text-[#555]" : "text-[#aaa]"}`}>
            {listing.timeAgo}
          </span>
        </div>

        {/* Title + desc */}
        <div>
          <h3 className={`font-syne text-sm font-semibold leading-snug mb-1.5 ${isDark ? "text-white" : "text-[#111]"}`}>
            {listing.title}
          </h3>
          <p className={`font-inter text-xs leading-relaxed line-clamp-2 ${isDark ? "text-[#555]" : "text-[#888]"}`}>
            {listing.description}
          </p>
        </div>

        {/* Prices */}
        <div className={`flex items-center justify-between rounded-xl px-3.5 py-2.5 ${isDark ? "bg-white/4" : "bg-black/4"}`}>
          <div className="text-center">
            <p className={`font-inter text-[9px] uppercase tracking-wider mb-0.5 ${isDark ? "text-[#555]" : "text-[#aaa]"}`}>Buy</p>
            <p className={`font-syne font-semibold text-sm ${isDark ? "text-[#888]" : "text-[#666]"}`}>${listing.buyPrice.toLocaleString()}</p>
          </div>
          <i className={`ri-arrow-right-line text-xs ${isDark ? "text-[#333]" : "text-[#ccc]"}`} />
          <div className="text-center">
            <p className={`font-inter text-[9px] uppercase tracking-wider mb-0.5 ${isDark ? "text-[#555]" : "text-[#aaa]"}`}>Sell</p>
            <p className={`font-syne font-semibold text-sm ${isDark ? "text-white" : "text-[#111]"}`}>${listing.sellPrice.toLocaleString()}</p>
          </div>
          <div className={`h-6 w-px ${isDark ? "bg-white/8" : "bg-black/8"}`} />
          <div className="text-center">
            <p className={`font-inter text-[9px] uppercase tracking-wider mb-0.5 ${isDark ? "text-[#555]" : "text-[#aaa]"}`}>Margin</p>
            <p className={`font-syne font-semibold text-sm ${marginColor}`}>+{margin}%</p>
          </div>
        </div>

        {/* Interest CTA */}
        <div className="flex items-center gap-2 mt-auto">
          <button
            type="button"
            onClick={() => !isInterested && onInterest(listing.id, listing.seller.name)}
            className={`flex-1 flex items-center justify-center gap-1.5 font-inter text-xs font-medium py-2.5 rounded-xl border transition-all duration-200 cursor-pointer whitespace-nowrap ${
              isInterested
                ? isDark ? "bg-white/8 border-white/15 text-white" : "bg-black/8 border-black/15 text-[#111]"
                : isDark ? "bg-transparent border-white/10 text-[#888] hover:bg-white/6 hover:text-white hover:border-white/20" : "bg-transparent border-black/10 text-[#888] hover:bg-black/5 hover:text-[#111] hover:border-black/20"
            }`}
          >
            <i className={`${isInterested ? "ri-heart-fill text-rose-400" : "ri-heart-line"} text-xs`} />
            {isInterested ? "Interested" : "I'm Interested"}
          </button>
          <div className={`flex items-center gap-1 px-3 py-2.5 rounded-xl border font-inter text-xs ${isDark ? "border-white/6 text-[#666]" : "border-black/8 text-[#aaa]"}`}>
            <i className="ri-eye-line text-xs" />
            {listing.interestedCount + (isInterested ? 1 : 0)}
          </div>
        </div>
      </div>
    </div>
  );
}
